import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Phone, PhoneCall, Calendar, MessageSquare, ArrowRight, Play, TrendingUp } from "lucide-react";

const GHL_CALENDAR = "https://api.leadconnectorhq.com/widget/booking/1O9DVPsKwdzHA4TLdUny";

const HERO_BG =
  "https://images.unsplash.com/photo-1705877883453-2b6685ff5864?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBnbG93aW5nJTIwZ2VvbWV0cmljJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODIyNTY5ODF8MA&ixlib=rb-4.1.0&q=85";

const AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop&crop=face",
];

const STAT_CARDS = [
  { label: "Calls Answered", value: "1,533", icon: PhoneCall },
  { label: "Appointments Booked", value: "1,144", icon: Calendar },
  { label: "Texts Replied", value: "2,156", icon: MessageSquare },
];

const RECENT = [
  { name: "Sarah Chen", tag: "Inbound Call", status: "Booked", color: "text-emerald-400", time: "2:34" },
  { name: "Michael T.", tag: "Outbound Call", status: "Follow-up", color: "text-amber-400", time: "1:08" },
  { name: "Priya K.", tag: "Web Chat", status: "In progress", color: "text-sky-400", time: "3:42" },
];

// Masked line-by-line reveal
const LINES = [
  [{ t: "The AI employee" }],
  [{ t: "that never sleeps" }],
  [{ t: "and " }, { t: "never misses a lead", grad: true }],
];

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const mockY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const mockOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.4]);

  // 3D mouse tilt on dashboard
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 150, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 150, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden pt-36 pb-24 md:pt-44"
    >
      {/* parallax background texture */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="pointer-events-none absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-25 hero-radial-mask" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-transparent to-[#050505]" />
        <div className="absolute left-1/2 top-24 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-brand/20 blur-[140px] animate-pulse-glow" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 backdrop-blur-sm"
        >
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs md:text-sm font-medium text-white/80">
            Your 24/7 AI receptionist — never miss a customer again
          </span>
        </motion.div>

        {/* kinetic masked headline */}
        <h1 className="font-heading mt-7 text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-[1.05] text-white max-w-4xl mx-auto">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.08em]">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
              >
                {line.map((seg, j) => (
                  <span key={j} className={seg.grad ? "text-gradient-brand" : ""}>{seg.t}</span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-6 text-base md:text-xl text-white/65 leading-relaxed max-w-2xl mx-auto"
        >
          One smart assistant that answers your phone, replies to every text and
          message, and books appointments for you — around the clock, so you
          never lose business to a missed call again.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65 }}
          className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href={GHL_CALENDAR}
            data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-base font-semibold text-white shadow-[0_0_32px_rgba(255,92,0,0.4)] transition-all duration-300 hover:bg-brand-hover hover:brightness-110 active:scale-95"
          >
            Get a Free Demo
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="#features"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-base font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/10 hover:border-white/40"
          >
            <Play className="h-4 w-4 fill-white" />
            See How It Works
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="mt-9 flex items-center justify-center gap-4"
        >
          <div className="flex -space-x-3">
            {AVATARS.map((a, i) => (
              <img key={i} src={a} alt="Business owner" className="h-10 w-10 rounded-full border-2 border-[#050505] object-cover" />
            ))}
          </div>
          <p className="text-sm text-white/55 text-left">
            Trusted by <span className="text-white font-semibold">500+</span> local
            <br className="hidden sm:block" /> business owners
          </p>
        </motion.div>

        {/* Dashboard mockup with parallax + 3D tilt + spotlight */}
        <motion.div
          style={{ y: mockY, opacity: mockOpacity }}
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-20 mx-auto max-w-5xl"
          data-testid="hero-dashboard"
        >
          <div className="absolute -inset-x-10 -top-10 bottom-0 bg-brand/10 blur-[100px] rounded-full" />
          <motion.div
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ rotateX, rotateY, transformPerspective: 1400, transformStyle: "preserve-3d" }}
            className="relative rounded-2xl border border-white/10 bg-[#0c0c0c]/90 backdrop-blur-xl p-4 md:p-6 shadow-2xl"
          >
            {/* spotlight sheen */}
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-[radial-gradient(600px_circle_at_50%_-10%,rgba(255,92,0,0.12),transparent_60%)]" />
            {/* window bar */}
            <div className="relative flex items-center gap-2 pb-4 border-b border-white/10">
              <span className="h-3 w-3 rounded-full bg-red-500/70" />
              <span className="h-3 w-3 rounded-full bg-amber-500/70" />
              <span className="h-3 w-3 rounded-full bg-emerald-500/70" />
              <span className="ml-3 font-mono text-xs text-white/40">app.5b.ai / dashboard</span>
            </div>

            <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-4 pt-5 text-left">
              <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {STAT_CARDS.map((s) => (
                  <div key={s.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <s.icon className="h-5 w-5 text-brand mb-3" />
                    <div className="font-heading text-2xl font-bold text-white">{s.value}</div>
                    <div className="text-xs text-white/45 mt-1">{s.label}</div>
                  </div>
                ))}
                <div className="sm:col-span-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-white/45">Bookings this month</span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400">
                      <TrendingUp className="h-3.5 w-3.5" /> +28%
                    </span>
                  </div>
                  <div className="flex items-end gap-1.5 h-20">
                    {[35, 50, 42, 65, 58, 80, 72, 95, 88, 76, 90, 100].map((h, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${h}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.5 + i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                        className="flex-1 rounded-t bg-gradient-to-t from-brand/30 to-brand"
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Phone className="h-4 w-4 text-brand" />
                  <span className="text-sm font-semibold text-white">Recent Calls</span>
                  <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
                  </span>
                </div>
                <div className="space-y-3">
                  {RECENT.map((r) => (
                    <div key={r.name} className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white">
                        {r.name[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-white truncate">{r.name}</div>
                        <div className="text-[11px] text-white/40">{r.tag}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-semibold ${r.color}`}>{r.status}</div>
                        <div className="font-mono text-[10px] text-white/35">{r.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
